<template>
  <Table.Row
    :id="isProcessing ? '' : item.id"
    class="group"
    :class="inactiveClasses"
    @click="$emit('rowClick', item)"
    @click.right="(event: MouseEvent) => $emit('rightClick', event, item)"
    @dblclick="$emit('folderClick', item)"
  >
    <Table.CheckboxCell
      v-if="showCheckbox"
      v-slot="{ checked }"
      class="bg-white"
    >
      <Checkbox
        v-if="!isProcessing"
        :size="22"
        :model-value="checked"
        class="tablet-h:invisible tablet-h:group-hover:visible"
        :class="checkboxClasses"
      />
    </Table.CheckboxCell>

    <Table.Cell>
      <ThumbnailIcon
        class="mr-2.5 min-w-9 max-w-9 h-9 tablet-h:min-w-6 tablet-h:max-w-6 tablet-h:h-6"
        :icon-size="thumbnailSize"
        :type="fileType"
        :image-url="thumbnailUrl"
      />
      <div class="w-full">
        <div v-truncate class="flex items-center">
          <div data-truncate-target @click.stop="$emit('nameClick', item)">
            {{ item.name }}
          </div>
          <div
            v-if="isCoOwner"
            v-tooltip="'Co-owned copyright'"
            class="ml-2 min-w-[22px] h-[22px] bg-gray-800/5 rounded-sm flex items-center justify-center"
          >
            <Icon name="CoOwner" :size="14" class="stroke-gray-800/60" />
          </div>
          <div
            v-if="isAuthor"
            v-tooltip="'Authored copyright'"
            class="ml-2 min-w-[22px] h-[22px] bg-gray-800/5 rounded-sm flex items-center justify-center"
          >
            <Icon name="Author" :size="14" class="stroke-[#7B7C7E]" />
          </div>
        </div>
        <div
          v-if="isFile"
          class="tablet-h:hidden text-[13px] flex items-center mt-0.5"
        >
          <div v-if="isMinting" class="font-secondary italic opacity-50">
            Processing
          </div>
          <div class="flex items-center" v-else>
            <Icon
              v-if="!isInactive && isFile"
              name="Dacr"
              :size="15"
              class="fill-gray-800/50 mr-1"
            />
            <div class="font-secondary opacity-50">
              {{ formatTimestamp(firstRegistrationTimestamp) }}
            </div>
            <div
              v-if="hasMoreRegistrations"
              class="font-secondary ml-1 text-[12px] text-gray-800"
              @click.stop="$emit('timestampClick', item)"
            >
              +{{ remainingRegistrations }} more
            </div>
          </div>
        </div>
      </div>
    </Table.Cell>

    <Table.Cell class="hidden tablet-h:flex">
      <div
        v-if="isMinting"
        class="font-secondary text-[14px] text-gray-800 italic opacity-50"
      >
        Processing
      </div>
      <div
        v-else
        class="relative flex items-center gap-x-2"
        :class="timestampClasses"
        @click.stop="$emit('timestampClick', item)"
      >
        <Icon v-if="!isInactive && isFile" name="Dacr" :size="18" />
        <span class="font-secondary">
          {{ formatTimestamp(firstRegistrationTimestamp) || '- -' }}
        </span>
        <div
          v-if="hasMoreRegistrations"
          class="flex items-center gap-x-1 text-[12px] leading-[18px] text-gray-500 font-medium"
        >
          <div class="font-secondary">+{{ remainingRegistrations }} more</div>
          <Icon
            name="ChevronUp"
            :size="10"
            class="stroke-gray-500 rotate-180"
          />
        </div>
      </div>
    </Table.Cell>

    <Table.Cell class="hidden tablet-h:flex">
      <template v-if="statuses.length">
        <Pill v-for="status in statuses" :key="status">
          {{ status }}
        </Pill>
      </template>
      <template v-else>- -</template>
    </Table.Cell>

    <Table.Cell
      class="tablet-h:hidden"
      @click.stop="$emit('threeDotsClick', item)"
    >
      <Icon
        :size="18"
        name="ThreeDots"
        class="fill-gray-800 stroke-gray-800"
        :class="threeDotsClasses"
      />
    </Table.Cell>

    <div
      v-if="showActions"
      class="hidden absolute top-0 right-0 group-hover:block pointer-events-auto"
    >
      <ItemActionButtons
        :item="item"
        :is-file="isFile"
        :selected="isSelected"
        @contextMenuToggled="
          (event: MouseEvent) => $emit('contextMenuClick', event, item)
        "
        @click.stop
      />
    </div>
  </Table.Row>
</template>

<script setup lang="ts">
import {
  isFile as isFileItem,
  isFolder as isFolderItem,
} from '@/features/explorer/utils/itemGuards'
import {
  CopyrightStatus,
  FileRole,
  type Item,
} from '@/shared/graphql/router/graphql'
import { Pill, ThumbnailIcon, Icon, Table, Checkbox } from '@dacr/ui'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { useDevice } from '@/shared/composables/useDevice'
import { computed } from 'vue'
import ItemActionButtons from './ItemActionButtons.vue'

const props = withDefaults(
  defineProps<{
    item: Item
    showCheckbox?: boolean
    checkboxClasses?: Record<string, boolean>
    isSelected?: boolean
  }>(),
  {
    showCheckbox: false,
    checkboxClasses: () => ({}),
    isSelected: false,
  }
)

defineEmits<{
  rowClick: [item: Item]
  rightClick: [event: MouseEvent, item: Item]
  folderClick: [item: Item]
  nameClick: [item: Item]
  timestampClick: [item: Item]
  threeDotsClick: [item: Item]
  contextMenuClick: [event: MouseEvent, item: Item]
}>()

const device = useDevice()

const isFile = computed(() => isFileItem(props.item))

const file = computed(() => (isFileItem(props.item) ? props.item : null))

const isMinting = computed(() => isFile.value && !file.value?.copyright?.id)

const isProcessing = computed(() => isFile.value && isMinting.value)

const isInactive = computed(
  () => file.value?.copyright?.status === CopyrightStatus.Inactive
)

const isCoOwner = computed(() => file.value?.role === FileRole.CoOwner)

const isAuthor = computed(() => file.value?.role === FileRole.Author)

const registrations = computed(() => file.value?.copyright?.registrations ?? [])

const firstRegistrationTimestamp = computed(
  () => registrations.value[0]?.timestamp
)

const hasMoreRegistrations = computed(() => registrations.value.length > 1)

const remainingRegistrations = computed(() => registrations.value.length - 1)

const thumbnailSize = computed(() =>
  device.isDesktop.value || device.isTabletHorizontal.value ? 24 : 36
)

const fileType = computed(() =>
  isFolderItem(props.item) ? 'folder' : file.value!.type
)

const thumbnailUrl = computed(() =>
  applyAuthorizationToken(file.value?.asset?.previewImageUrl, [
    { key: 'width', value: 36 },
  ])
)

const inactiveClasses = computed(() => ({
  'opacity-50': isInactive.value,
}))

const showActions = computed(() => !isProcessing.value && !isInactive.value)

const threeDotsClasses = computed(() => ({
  invisible: props.showCheckbox || isProcessing.value,
}))

const statuses = computed(() => {
  if (isProcessing.value) return []
  // Status pills that depended on legacy fields (licenseStatus, transferStatus, saleStatus)
  // are not available in the router schema yet
  return [] as string[]
})

const timestampClasses = computed(() => ({
  ...(registrations.value.length > 1 && {
    "after:hidden hover:after:block after:rounded-md cursor-pointer after:content-[''] after:w-[calc(100%+14px)] after:h-[30px] after:absolute after:top-[-6px] after:left-[-7px]":
      true,
    'after:bg-green-700/15': props.isSelected,
    'after:bg-gray-800/8': !props.isSelected,
  }),
}))
</script>
